package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.CreatedShotReportShareDto
import dev.lingvo.shared.ShotReportShareDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.ApiConfig
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LOneTimeSecret
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.ProjectAuthoritySubject

/** Public URLs exist only in this keyed component's memory, never the share list or browser storage. */
@Composable
internal fun ScreenshotReportShares(
    slug: String, subject: ProjectAuthoritySubject?, platform: StorePlatform, runner: ScreenshotUpdatesRunner,
) {
    var reload by remember { mutableStateOf(0) }
    var days by remember { mutableStateOf("7") }
    var busy by remember { mutableStateOf(false) }
    var failure by remember { mutableStateOf<String?>(null) }
    var created by remember { mutableStateOf<CreatedShotReportShareDto?>(null) }
    var revoking by remember { mutableStateOf<ShotReportShareDto?>(null) }
    var uncertain by remember { mutableStateOf(false) }
    val loaded = loadReloadable(subject, platform, reloadToken = reload) {
        readScreenshotUpdates(subject, slug) { ScreenshotUpdatesApi.shares(slug, platform) }
    }
    LSectionHeading(t("app.projects.slug.screenshots.updates_shares.share_aggregate_reports", "Share aggregate reports"),
        t("app.projects.slug.screenshots.updates_shares.create_an_expiring_report_link_for", "Create an expiring report link for this project and platform, or revoke an existing link."),
        actions = { LButton(t("app.projects.slug.screenshots.updates_shares.refresh_links", "Refresh links"), { reload++ },
            enabled = !busy && !loaded.refreshing, variant = LButtonVariant.SECONDARY) })
    LAlert(t("app.projects.slug.screenshots.updates_shares.anyone_holding_a_link_or_badge", "Anyone holding a link or badge can read its report"),
        t("app.projects.slug.screenshots.updates_shares.reports_and_badges_expose_only_aggregate", "Reports and badges expose only aggregate counts. They include no screenshots, captions, project names, people or image download URLs. Embedding a badge shares its link with viewers. Access ends when the link expires or is revoked."),
        variant = LAlertVariant.INFO)
    loaded.refreshError?.let { AsyncFailureState(it, subject = t("app.projects.slug.screenshots.updates_shares.report_links", "report links"), onRetry = { reload++ }) }
    when (val state = loaded.state) {
        Async.Loading -> LoadingState(rows = 2)
        is Async.Failure -> AsyncFailureState(state, subject = t("app.projects.slug.screenshots.updates_shares.report_links", "report links"), onRetry = { reload++ })
        is Async.Success -> {
            if (state.value.isEmpty()) LMuted(t("app.projects.slug.screenshots.updates_shares.no_report_links_have_been_created", "No report links have been created for this platform."))
            state.value.forEach { share ->
                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                        LMuted(tf("screenshots.updates.share_created", "Created: {when}", "when" to share.createdAt))
                        LMuted(tf("screenshots.updates.share_expires", "Expires: {when}", "when" to share.expiresAt))
                        if (share.revokedAt != null) LMuted(tf("screenshots.updates.share_revoked", "Revoked: {when}", "when" to share.revokedAt))
                        else LButton(t("app.projects.slug.screenshots.updates_shares.revoke_link", "Revoke link"), { failure = null; revoking = share },
                            enabled = !busy, variant = LButtonVariant.GHOST, modifier = updatesControlModifier(),
                            accessibleLabel = tf("screenshots.updates.revoke_named", "Revoke report link created {when}", "when" to share.createdAt))
                    }
                }
            }
        }
    }
    failure?.let { LAlert(it, variant = LAlertVariant.WARNING) }
    if (uncertain) {
        LMuted(t("app.projects.slug.screenshots.updates_shares.creation_may_have_completed_before_the", "Creation may have completed before the connection failed. Check the refreshed list and revoke any unwanted link before trying again."))
        LButton(t("app.projects.slug.screenshots.updates_shares.ive_checked_the_links_allow_another", "I've checked the links; allow another attempt"), { uncertain = false },
            enabled = !busy && !loaded.refreshing && loaded.state is Async.Success && loaded.refreshError == null,
            variant = LButtonVariant.GHOST, modifier = updatesControlModifier())
    }
    if (created == null) {
        LSelect(days, listOf(
            LSelectOption("1", t("app.projects.slug.screenshots.updates_shares.1_day", "1 day")),
            LSelectOption("7", t("app.projects.slug.screenshots.updates_shares.7_days", "7 days")),
            LSelectOption("30", t("app.projects.slug.screenshots.updates_shares.30_days", "30 days")),
            LSelectOption("90", t("app.projects.slug.screenshots.updates_shares.90_days", "90 days")),
        ), { days = it }, label = t("app.projects.slug.screenshots.updates_shares.report_link_expires_after", "Report link expires after"), enabled = !busy,
            modifier = Modifier.fillMaxWidth())
        LButton(t("app.projects.slug.screenshots.updates_shares.create_report_link", "Create report link"), {
            runner.run(ScreenshotUpdatesAction.REPORT_SHARE, { busy = it }, {
                ScreenshotUpdatesApi.createShare(slug, platform, days.toInt())
            }, {
                failure = shotRequestFailure(it); uncertain = true; reload++
            }, { response ->
                val origin = ApiConfig.baseUrl.removeSuffix("/api/v1")
                if (auditShareMarkup(origin + response.reportPath, origin + response.badgePath) == null) {
                    failure = tr("app.projects.slug.screenshots.updates_shares.the_link_response_could_not_be", "The link response could not be verified. Refresh the list and revoke the new link before creating another.")
                    uncertain = true
                } else { created = response; failure = null; uncertain = false }
                reload++
            })
        }, enabled = !busy && !uncertain && !loaded.refreshing && loaded.state is Async.Success && loaded.refreshError == null,
            loading = busy, modifier = updatesControlModifier())
    }
    created?.let { issued ->
        val origin = ApiConfig.baseUrl.removeSuffix("/api/v1")
        val reportUrl = origin + issued.reportPath
        val markup = auditShareMarkup(reportUrl, origin + issued.badgePath)
        if (markup != null) LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                LAlert(t("app.projects.slug.screenshots.updates_shares.copy_this_report_link_and_badge", "Copy this report link and badge code now"),
                    t("app.projects.slug.screenshots.updates_shares.they_are_shown_only_here_once", "They are shown only here, once. The link list cannot recover them after you leave or close this panel."), variant = LAlertVariant.WARNING)
                LOneTimeSecret(reportUrl, t("app.projects.slug.screenshots.updates_shares.report_url", "Report URL"), t("app.projects.slug.screenshots.updates_shares.copy_report_url", "Copy report URL"),
                    onCopyFailed = { failure = clipboardFailure() })
                LOneTimeSecret(markup, t("app.projects.slug.screenshots.updates_shares.badge_embed_code", "Badge embed code"), t("app.projects.slug.screenshots.updates_shares.copy_badge_code", "Copy badge code"),
                    onCopyFailed = { failure = clipboardFailure() })
                LMuted(tf("screenshots.updates.share_expires", "Expires: {when}", "when" to issued.share.expiresAt))
                LButton(t("app.projects.slug.screenshots.updates_shares.ive_saved_the_link_and_code", "I've saved the link and code"), { created = null },
                    variant = LButtonVariant.SECONDARY, modifier = updatesControlModifier())
            }
        }
    }
    LConfirmDialog(
        open = revoking != null,
        title = t("app.projects.slug.screenshots.updates_shares.revoke_this_report_link", "Revoke this report link?"),
        confirmLabel = t("app.projects.slug.screenshots.updates_shares.revoke_link", "Revoke link"),
        tone = LConfirmTone.NEUTRAL,
        consequenceTitle = t("app.projects.slug.screenshots.updates_shares.the_report_and_its_badge_stop", "The report and its badge stop working"),
        consequence = t("app.projects.slug.screenshots.updates_shares.anyone_using_this_link_or_its", "Anyone using this link or its badge will lose access. Previously copied aggregate counts cannot be withdrawn."),
        busy = busy, error = failure,
        onCancel = { if (!busy) revoking = null },
        onConfirm = {
            revoking?.let { share ->
                runner.run(ScreenshotUpdatesAction.REPORT_SHARE, { busy = it }, {
                    ScreenshotUpdatesApi.revokeShare(slug, platform, share.id)
                }, { failure = shotRequestFailure(it) }, {
                    if (created?.share?.id == share.id) created = null
                    revoking = null; failure = null; reload++
                })
            }
        },
    )
}

private fun clipboardFailure() = tr("app.projects.slug.screenshots.updates_shares.couldnt_reach_the_clipboard_select_the", "Couldn't reach the clipboard. Select the value and copy it manually before leaving.")
